import { useState, useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { supplementsAPI } from '../services/api'
import { PrinterIcon } from '@heroicons/react/24/outline'
import { contentLanguage } from '../i18n/supported'

export default function SupplementHistoryReport() {
  const { t, i18n } = useTranslation()

  // Default date range: start of current year to today
  const today = new Date()
  const startOfYear = new Date(today.getFullYear(), 0, 1)

  const [dateRange, setDateRange] = useState({
    start: startOfYear.toISOString().split('T')[0],
    end: today.toISOString().split('T')[0]
  })

  // State for translated notes (keyed by user_supplement_id)
  const [translatedNotes, setTranslatedNotes] = useState({})

  // Fetch all user supplements
  const { data: userSupplements, isLoading, error } = useQuery({
    queryKey: ['user-supplements-all'],
    queryFn: async () => {
      const response = await supplementsAPI.getUserSupplements()
      return response.data?.user_supplements || []
    }
  })

  // Fetch translated notes when supplements load or language changes
  useEffect(() => {
    const fetchTranslatedNotes = async () => {
      if (!userSupplements) return

      const currentLang = contentLanguage(i18n?.language)
      const supplementsWithNotes = userSupplements.filter(us => us.notes)

      if (currentLang === 'en') {
        const notesMap = {}
        supplementsWithNotes.forEach(us => {
          notesMap[us.id] = us.notes
        })
        setTranslatedNotes(notesMap)
        return
      }

      const translations = {}
      for (const userSupplement of supplementsWithNotes) {
        try {
          const response = await supplementsAPI.translateNotes(userSupplement.id, currentLang)
          translations[userSupplement.id] = response.data.translated_notes
        } catch (err) {
          console.warn(`[SupplementHistoryReport] Could not translate notes for ${userSupplement.id}:`, err)
          translations[userSupplement.id] = userSupplement.notes
        }
      }
      setTranslatedNotes(translations)
    }

    fetchTranslatedNotes()
  }, [userSupplements, i18n.language])

  // Get locale for date formatting - extensible for new languages
  const getLocale = () => {
    const lang = i18n.language || 'en'
    const localeMap = {
      'en': 'en-US',
      'de': 'de-DE',
      'pt-BR': 'pt-BR',
      'pt': 'pt-BR',
      'fr': 'fr-FR',
      'es': 'es-ES',
      'it': 'it-IT'
    }
    return localeMap[lang] || localeMap[lang.split('-')[0]] || 'en-US'
  }

  // Translate unit labels
  const translateUnit = (unit) => {
    const unitKey = `supplements.units.${unit}Lower`
    const translated = t(unitKey)
    return translated !== unitKey ? translated : unit
  }

  // Translate dosage form labels
  const translateForm = (form) => {
    if (!form) return ''
    const formKey = `supplements.forms.${form}`
    const translated = t(formKey)
    return translated !== formKey ? translated : form
  }

  // Calculate duration in days
  const calculateDuration = (startDate, endDate) => {
    const start = new Date(startDate)
    const end = endDate ? new Date(endDate) : new Date()
    const days = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1
    return days
  }

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return t('historyReport.ongoing')
    return new Date(dateString).toLocaleDateString(getLocale(), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const handleDateChange = (field, value) => {
    setDateRange(prev => ({ ...prev, [field]: value }))
  }

  const handlePrint = () => {
    window.print()
  }

  // Use useMemo to ensure filtering updates when dateRange changes
  // Must be called before any early returns to follow Rules of Hooks
  const filteredSupplements = useMemo(() => {
    if (!userSupplements || !Array.isArray(userSupplements)) return []

    const rangeStart = new Date(dateRange.start)
    const rangeEnd = new Date(dateRange.end)

    return userSupplements.filter(s => {
      const suppStart = new Date(s.start_date)
      const suppEnd = s.end_date ? new Date(s.end_date) : null

      // Supplement was active at some point during the range
      return suppStart <= rangeEnd && (suppEnd === null || suppEnd >= rangeStart)
    })
  }, [userSupplements, dateRange.start, dateRange.end])

  const activeCount = filteredSupplements.filter(s => s.is_active).length
  const stoppedCount = filteredSupplements.filter(s => !s.is_active).length
  const supplementsWithNotes = filteredSupplements.filter(s => s.notes)

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <p className="text-red-800 text-lg font-semibold">Error loading supplements</p>
          <p className="text-red-600 mt-2">{error.message}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header - Hidden when printing */}
      <div className="mb-6 print:hidden">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('historyReport.title')}</h1>
          </div>
          <button
            onClick={handlePrint}
            className="btn-primary flex items-center gap-2"
          >
            <PrinterIcon className="h-5 w-5" />
            {t('historyReport.print')}
          </button>
        </div>

        {/* Date Range Picker */}
        <div className="mt-4 flex flex-wrap items-center gap-4">
          <label className="text-sm font-medium text-gray-700">{t('historyReport.dateRange')}:</label>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => handleDateChange('start', e.target.value)}
              className="form-input"
            />
            <span className="text-gray-500">—</span>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => handleDateChange('end', e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="form-input"
            />
          </div>
        </div>
      </div>

      {/* Print Header - Only visible when printing */}
      <div className="hidden print:block mb-4">
        <h1 className="text-xl font-bold text-gray-900 mb-1">
          {t('historyReport.title')} — {formatDate(dateRange.start)} - {formatDate(dateRange.end)}
        </h1>
        <p className="text-xs text-gray-500">{t('historyReport.generated')}: {new Date().toLocaleString(getLocale())}</p>
      </div>

      {/* Summary Card - Hidden when printing */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-6 mb-6 text-white print:hidden">
        <div className="grid grid-cols-3 gap-4">
          <div>
            <div className="text-3xl font-bold">{filteredSupplements.length}</div>
            <div className="text-sm opacity-90">{t('historyReport.supplements')}</div>
          </div>
          <div>
            <div className="text-3xl font-bold">{activeCount}</div>
            <div className="text-sm opacity-90">{t('historyReport.active')}</div>
          </div>
          <div>
            <div className="text-3xl font-bold">{stoppedCount}</div>
            <div className="text-sm opacity-90">{t('historyReport.stopped')}</div>
          </div>
        </div>
      </div>

      {/* Print Summary */}
      <div className="hidden print:block mb-4 text-sm text-gray-600">
        {t('historyReport.supplements')}: {filteredSupplements.length} | {t('historyReport.active')}: {activeCount} | {t('historyReport.stopped')}: {stoppedCount}
      </div>

      {filteredSupplements.length === 0 ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
          <p className="text-yellow-800 text-lg">{t('historyReport.noSupplements')}</p>
          <p className="text-yellow-600 mt-2">{t('historyReport.adjustDateRange')}</p>
        </div>
      ) : (
        <>
          {/* Main Table */}
          <div className="bg-surface-container rounded-lg shadow-md overflow-hidden border border-gray-200 mb-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      {t('historyReport.name')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      {t('historyReport.form')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      🌅 {t('historyReport.morning')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      ☀️ {t('historyReport.midday')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      🌙 {t('historyReport.evening')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      {t('historyReport.period')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                      {t('historyReport.status')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-surface-container divide-y divide-gray-200">
                  {filteredSupplements.map((userSupplement, index) => {
                    const supplement = userSupplement.supplement
                    const duration = calculateDuration(userSupplement.start_date, userSupplement.end_date)

                    return (
                      <tr key={userSupplement.id} className={index % 2 === 0 ? 'bg-surface-container' : 'bg-gray-50'}>
                        <td className="px-4 py-3 border-r border-gray-200">
                          <div className="font-semibold text-gray-900">{supplement.name}</div>
                          {supplement.brand && (
                            <div className="text-sm text-gray-600">{supplement.brand}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center border-r border-gray-200 text-sm text-gray-700">
                          {translateForm(supplement.dosage_form)}
                        </td>
                        <td className="px-4 py-3 text-center border-r border-gray-200">
                          {userSupplement.morning_quantity > 0 ? (
                            <div className="flex flex-col items-center">
                              <span className="font-bold text-gray-900">{userSupplement.morning_quantity}</span>
                              <span className="text-xs text-gray-500">{translateUnit(userSupplement.quantity_unit)}</span>
                            </div>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center border-r border-gray-200">
                          {userSupplement.midday_quantity > 0 ? (
                            <div className="flex flex-col items-center">
                              <span className="font-bold text-gray-900">{userSupplement.midday_quantity}</span>
                              <span className="text-xs text-gray-500">{translateUnit(userSupplement.quantity_unit)}</span>
                            </div>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center border-r border-gray-200">
                          {userSupplement.evening_quantity > 0 ? (
                            <div className="flex flex-col items-center">
                              <span className="font-bold text-gray-900">{userSupplement.evening_quantity}</span>
                              <span className="text-xs text-gray-500">{translateUnit(userSupplement.quantity_unit)}</span>
                            </div>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center border-r border-gray-200 text-sm">
                          <div className="text-gray-900">
                            {formatDate(userSupplement.start_date)}
                          </div>
                          <div className="text-gray-500">
                            → {formatDate(userSupplement.end_date)}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {userSupplement.is_active ? (
                            <div className="flex flex-col items-center">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                ● {t('historyReport.active')}
                              </span>
                              <span className="text-xs text-gray-500 mt-1">
                                ({duration} {t('historyReport.days')})
                              </span>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                ○ {t('historyReport.stopped')}
                              </span>
                              <span className="text-xs text-gray-500 mt-1">
                                ({duration} {t('historyReport.days')})
                              </span>
                            </div>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Notes Section */}
          <div className="bg-surface-container rounded-lg shadow-md overflow-hidden border border-gray-200">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">{t('historyReport.notesSection')}</h2>
            </div>
            <div className="p-4">
              {supplementsWithNotes.length === 0 ? (
                <p className="text-gray-500 italic">{t('historyReport.noNotes')}</p>
              ) : (
                <div className="space-y-3">
                  {supplementsWithNotes.map(userSupplement => (
                    <div key={userSupplement.id} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                      <span className="font-semibold text-gray-900">{userSupplement.supplement.name}:</span>
                      <span className="text-gray-600 ml-2">
                        {translatedNotes[userSupplement.id] || userSupplement.notes}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
            margin: 0 !important;
            padding: 0 !important;
          }

          @page {
            margin: 1.5cm;
            size: A4 landscape;
          }

          * {
            box-sizing: border-box;
          }

          .p-6 {
            padding-top: 0 !important;
            margin-top: 0 !important;
          }

          table {
            page-break-inside: auto;
            margin-top: 0 !important;
            font-size: 11px;
          }

          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }

          thead {
            display: table-header-group;
          }

          tfoot {
            display: table-footer-group;
          }

          .rounded-full {
            border-radius: 9999px;
          }

          .bg-green-100 {
            background-color: #dcfce7 !important;
          }

          .bg-gray-100 {
            background-color: #f3f4f6 !important;
          }
        }
      `}</style>
    </div>
  )
}
